'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { questService, Achievement, UserAchievement } from '@/lib/questService';

interface AchievementContextType {
  allAchievements: Achievement[];
  userAchievements: UserAchievement[];
  loading: boolean;
  error: string | null;
  refreshAchievements: () => Promise<void>;
  awardAchievement: (achievementId: string) => Promise<void>;
  getUserAchievementCount: () => number;
}

const AchievementContext = createContext<AchievementContextType | undefined>(undefined);

export function AchievementProvider({ children }: { children: React.ReactNode }) {
  const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAchievements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [allAchievementsData, userAchievementsData] = await Promise.all([
        questService.getAllAchievements(),
        questService.getUserAchievements()
      ]);

      setAllAchievements(allAchievementsData);
      setUserAchievements(userAchievementsData);
    } catch (err) {
      console.error('Error fetching achievements:', err);
      setError('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAchievements = useCallback(async () => {
    await fetchAchievements();
  }, [fetchAchievements]);

  const awardAchievement = useCallback(async (achievementId: string) => {
    try {
      await questService.awardAchievement(achievementId);
      // Refresh achievements to get the latest data
      await refreshAchievements();
    } catch (err) {
      console.error('Error awarding achievement:', err);
      throw err;
    }
  }, [refreshAchievements]);

  const getUserAchievementCount = useCallback(() => {
    return userAchievements.length;
  }, [userAchievements]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  const value: AchievementContextType = {
    allAchievements,
    userAchievements,
    loading,
    error,
    refreshAchievements,
    awardAchievement,
    getUserAchievementCount,
  };

  return (
    <AchievementContext.Provider value={value}>
      {children}
    </AchievementContext.Provider>
  );
}

export function useAchievements() {
  const context = useContext(AchievementContext);
  if (context === undefined) {
    throw new Error('useAchievements must be used within an AchievementProvider');
  }
  return context;
}